import React from 'react';
import {Accordion, Badge} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Card} from 'src/design/atoms/Card';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
  SkillSignOffCriterionResultEnum,
  SkillSignOffResultEnum,
} from 'src/api/ProactApiClient';
import {Spinner} from 'src/design/atoms/Spinner';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetSkillSignOffQuery} from 'src/redux/queries/skillSignOff';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useTrainingBreadcrumbs} from 'src/shared/hooks/useTrainingBreadcrumbs';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';

const CRITERION_RESULT_COLOR: Record<SkillSignOffCriterionResultEnum, string> = {
  [SkillSignOffCriterionResultEnum.Invalid]: 'gray',
  [SkillSignOffCriterionResultEnum.Pass]: 'green',
  [SkillSignOffCriterionResultEnum.Fail]: 'red',
  [SkillSignOffCriterionResultEnum.NotApplicable]: 'gray',
};

const CRITERION_RESULT_LABEL: Record<SkillSignOffCriterionResultEnum, string> = {
  [SkillSignOffCriterionResultEnum.Invalid]: 'Unknown',
  [SkillSignOffCriterionResultEnum.Pass]: 'Pass',
  [SkillSignOffCriterionResultEnum.Fail]: 'Fail',
  [SkillSignOffCriterionResultEnum.NotApplicable]: 'N/A',
};

/**
 * Read-only detail view for a completed (or pending) sign-off — criterion
 * snapshot results (`criterionText`/`criterionEvidenceType` captured at
 * evaluation time, not the live skill definition), evaluator, dates,
 * comment and previous-attempt panel (prior Not Yet Competent, same
 * skill+trainee). Entry points: matrix cell click-through (Skill Matrix
 * tab, when the cell carries a `signOffId`), "already evaluated" branch of
 * the evaluate page. My Skills history renders inline instead of linking
 * here — this route requires Training:View, which most trainees don't
 * have.
 */
export const SignOffDetailPage: React.FC = () => {
  useSetDocumentTitle('Training');
  const {signOffId} = useParams();
  const parsedId = signOffId ? parseInt(signOffId) : undefined;

  const {
    data: signOff,
    isLoading,
    error,
  } = useGetSkillSignOffQuery(parsedId!, {
    skip: !parsedId,
  });


  const {breadcrumbs, fallbackTo} = useTrainingBreadcrumbs({
    mode: 'sign-off-detail',
    signOffSubject: signOff?.userName,
  });

  const resultLabel =
    signOff?.result === SkillSignOffResultEnum.SignedOff
      ? 'Signed off'
      : signOff?.result === SkillSignOffResultEnum.NotYetCompetent
      ? 'Not yet competent'
      : 'Pending';
  const resultColor =
    signOff?.result === SkillSignOffResultEnum.SignedOff
      ? 'green'
      : signOff?.result === SkillSignOffResultEnum.NotYetCompetent
      ? 'red'
      : 'gray';

  // Server sends explicit null for "no prior attempt" — normalize to
  // undefined rather than trusting the generated client's `| undefined`.
  const previousAttemptComment = signOff?.previousAttemptComment ?? undefined;
  const previousAttemptAt = signOff?.previousAttemptAt ?? undefined;
  const previousAttemptByName = signOff?.previousAttemptByName ?? undefined;
  const hasPreviousAttempt =
    previousAttemptComment !== undefined || previousAttemptAt !== undefined;

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Training}>
      <BackBar fallbackTo={fallbackTo} fallbackLabel="Back to Sign-offs" breadcrumbs={breadcrumbs} />

      {error ? (
        <Alert color="red" mt="md">
          {parseErrorMessage(error)}
        </Alert>
      ) : isLoading || !signOff ? (
        <Spinner loadingText="Loading sign-off..." />
      ) : (
        <Stack gap="md" mt="md">
          <Group justify="space-between" wrap="wrap">
            <Box>
              <Text fw={700} size="xl">
                {signOff.userName}
              </Text>
              <Text c="dimmed">{signOff.skillName}</Text>
            </Box>
            <Badge color={resultColor} size="lg">
              {resultLabel}
            </Badge>
          </Group>

          <Card withBorder shadow="xs" p="md">
            <Stack gap={4}>
              <Text size="sm">
                Requested {parseUtcDate(signOff.requestedAt).toLocaleDateString()}
              </Text>
              {signOff.signedOffAt && (
                <Text size="sm">
                  Evaluated {parseUtcDate(signOff.signedOffAt).toLocaleDateString()}
                  {signOff.signedOffByName ? ` by ${signOff.signedOffByName}` : ''}
                </Text>
              )}
              {signOff.expiresAt && (
                <Text size="sm">
                  Expires {parseUtcDate(signOff.expiresAt).toLocaleDateString()}
                </Text>
              )}
              {signOff.comment && (
                <Text size="sm" c="dimmed" mt={4}>
                  Comment: {signOff.comment}
                </Text>
              )}
            </Stack>
          </Card>

          {hasPreviousAttempt && (
            <Accordion variant="separated">
              <Accordion.Item value="previous-attempt">
                <Accordion.Control>
                  Previous attempt: Not yet competent
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap={4}>
                    {previousAttemptAt && (
                      <Text size="sm" c="dimmed">
                        {parseUtcDate(previousAttemptAt).toLocaleDateString()}
                        {previousAttemptByName ? ` · ${previousAttemptByName}` : ''}
                      </Text>
                    )}
                    {previousAttemptComment && (
                      <Text size="sm">{previousAttemptComment}</Text>
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          )}

          <Stack gap="sm">
            {signOff.criterionResults.length === 0 ? (
              <Alert color="gray">No criterion results are recorded yet.</Alert>
            ) : (
              signOff.criterionResults.map(criterionResult => (
                <Card key={criterionResult.criterionId} withBorder shadow="xs" p="md">
                  <Group justify="space-between" align="flex-start" wrap="wrap">
                    <Text fw={500}>{criterionResult.criterionText}</Text>
                    <Badge color={CRITERION_RESULT_COLOR[criterionResult.result]}>
                      {CRITERION_RESULT_LABEL[criterionResult.result]}
                    </Badge>
                  </Group>
                  {criterionResult.note && (
                    <Text size="sm" c="dimmed" mt="xs">
                      {criterionResult.note}
                    </Text>
                  )}
                </Card>
              ))
            )}
          </Stack>
        </Stack>
      )}
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.TrainingSignOffDetail(':signOffId'),
  element: <SignOffDetailPage />,
  module: ModuleEnum.Training,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});

export default SignOffDetailPage;
